import {
  type AddPartnerLedgerEntryInput,
  type PartnerLedger,
  type PartnerLedgerDirection,
  type PartnerLedgerMethod,
} from "@lumora/bridge";
import { DataTable, useViewModel } from "@lumora/ui";
import { useEffect, useState } from "react";
import { usePartnerLedgerVm } from "./platformViewModels.js";
import { ConfirmGate } from "./ConfirmGate.js";
import {
  balanceLine,
  dollarsToCents,
  LEDGER_COLUMNS,
  ledgerRows,
} from "./partnerView.js";
import { StatusMessage } from "./SiteAccountsList.js";

/** Presentational balance + table. `ledger === null` while loading. */
export function LedgerView({ ledger }: { ledger: PartnerLedger | null }) {
  if (!ledger) {
    return (
      <p className="muted platform-provision-status" role="status">
        Loading ledger…
      </p>
    );
  }
  const bal = balanceLine(ledger.totals.netToPartnerCents);
  return (
    <>
      <p className="platform-provision-status" role="status" aria-live="polite">
        <strong>{bal.line}</strong>
        <br />
        <span className="muted">{bal.explain}</span>
      </p>
      <DataTable
        columns={LEDGER_COLUMNS}
        rows={ledgerRows(ledger.entries)}
        empty="No payments logged with this partner yet."
      />
    </>
  );
}

/** Admin: running ledger of money paid to / received from one delivery partner. */
export function PartnerLedgerPanel({
  slug,
  connectReady,
}: {
  slug: string;
  connectReady: boolean | null;
}) {
  const vm = usePartnerLedgerVm(slug);
  const { ledger, message: msg, error: err } = useViewModel(vm);
  const [direction, setDirection] =
    useState<PartnerLedgerDirection>("to_partner");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PartnerLedgerMethod>("cash");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [siteSlug, setSiteSlug] = useState("");

  useEffect(() => {
    void vm.load();
  }, [vm]);

  return (
    <section className="ops-panel platform-provision-card" data-partner-ledger>
      <h3>Partner ledger</h3>
      <p className="muted">
        Money you paid this partner and money they paid you (cash, check, wire,
        or automatic Stripe transfers).
      </p>
      <p className="muted platform-provision-status" role="status">
        Stripe onboarding:{" "}
        {connectReady === null
          ? "unknown"
          : connectReady
            ? "complete"
            : "not complete"}
      </p>
      <LedgerView ledger={ledger} />

      <h4>Add entry</h4>
      <form
        className="site-signin"
        onSubmit={(e) => {
          e.preventDefault();
          vm.clearStatus();
          const cents = dollarsToCents(amount);
          if (cents === null) {
            vm.fail("Enter a valid dollar amount");
            return;
          }
          const input: AddPartnerLedgerEntryInput = {
            direction,
            amountCents: cents,
            method,
          };
          if (note.trim()) input.note = note.trim();
          if (siteSlug.trim()) input.siteSlug = siteSlug.trim();
          if (date)
            input.occurredAt = new Date(`${date}T12:00:00`).toISOString();
          void vm.addEntry(input).then((added) => {
            if (added) {
              setAmount("");
              setNote("");
            }
          });
        }}
      >
        <label>
          Direction
          <select
            value={direction}
            onChange={(e) =>
              setDirection(e.target.value as PartnerLedgerDirection)
            }
          >
            <option value="to_partner">I paid partner</option>
            <option value="from_partner">Partner paid me</option>
          </select>
        </label>
        <label>
          Amount (USD)
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>
        <label>
          Method
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PartnerLedgerMethod)}
          >
            <option value="cash">Cash</option>
            <option value="check">Check</option>
            <option value="wire">Wire</option>
            <option value="stripe_transfer">Stripe transfer</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Date (optional)
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <label>
          Site slug (optional)
          <input
            type="text"
            value={siteSlug}
            onChange={(e) => setSiteSlug(e.target.value)}
          />
        </label>
        <label>
          Note
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <ConfirmGate
          idleLabel="Add ledger entry"
          confirmLabel="Yes, add entry"
          confirmType="submit"
          onConfirm={() => {}}
        />
      </form>
      <StatusMessage msg={msg} err={err} />
    </section>
  );
}
